import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import { getApiBaseUrl } from '../../../core/config/api';
import { useInterventionsList } from './hooks/useInterventionsList';
import { submitInterventionScenario } from './hooks/submitInterventionScenario';
import VehiculesByServicePicker from './hooks/VehiculesByServicePicker';
import InterventionParamsSection from './hooks/InterventionParamsSection';
import { useInterventionFormData } from './hooks/useInterventionFormData';
import configService from '../../../shared/services/configService';
import type { Service, VehiculeConfig } from '../../../shared/types/config';

export interface CategorieMission {
  id: number;
  nom: string;
  label?: string;
}

/** Conditions d'unlock définies sur le scénario (`notes_cta.panel_meta.conditions_spec`). */
export interface InterventionConditionsSpec {
  batiments: { config_building_id: number; quantite_min: number }[];
  /** Réservé rétrocompat / anciennes données ; le formulaire n'envoie plus de compétences ici. */
  competence_ids?: number[];
}

/** Réponse GET /api/cta/interventions/:id pour préremplir le formulaire */
export interface PanelInterventionDetail {
  id: number;
  service_id: number;
  titres: string[];
  types_mission: number[];
  /** Ancien mode : IDs `cta_conditions` (optionnel si tout est en conditions_spec). */
  conditions: number[];
  conditions_spec: InterventionConditionsSpec;
  statut: string;
  is_tuto: boolean;
  is_exceptionnelle: boolean;
  params: {
    temps_max: number;
    victimes_min: number;
    victimes_max: number;
    hostiles_min: number;
    hostiles_max: number;
    vehicules_accidentes_min: number;
    vehicules_accidentes_max: number;
  };
  recompense: { xp: number; credits: number };
  evolutions: { id: number; taux: number }[];
  vehicules_requis: { id_vehicule: number; quantite: number }[];
  memo?: string;
  narrative_appel?: string | null;
  narrative_revelations?: Array<{ phase: string; texte: string }> | null;
}

const inputCls = 'w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60';
const labelCls = 'block text-xs font-medium text-slate-400 mb-1.5';

const InterventionsCreate: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editIdParam } = useParams<{ id: string }>();
  const isEmbedded = embedded || new URLSearchParams(location.search).get('embed') === '1';
  const editingId = editIdParam ? parseInt(editIdParam, 10) : NaN;
  const isEditMode = Number.isInteger(editingId) && editingId > 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [conditionBatimentRows, setConditionBatimentRows] = useState<
    { config_building_id: number; quantite_min: number }[]
  >([{ config_building_id: 0, quantite_min: 1 }]);
  const [evolutionTaux, setEvolutionTaux] = useState<Record<number, number>>({});
  const [openServiceId, setOpenServiceId] = useState<number | null>(null);
  const [vehiculeSearchQuery, setVehiculeSearchQuery] = useState('');
  const [vehiculeQuantites, setVehiculeQuantites] = useState<Record<number, number>>({});
  const [scenarioKind, setScenarioKind] = useState<'aucun' | 'tuto' | 'exceptionnelle'>('aucun');
  const [narrativeAppel, setNarrativeAppel] = useState('');
  const [narrativeRevelations, setNarrativeRevelations] = useState<
    Array<{ phase: string; texte: string }>
  >([]);

  // ── Prévisualisation ────────────────────────────────────────────────────────
  const [showPreview, setShowPreview] = useState(false);
  interface PreviewSnapshot {
    titre: string;
    narrativeAppel: string;
    narrativeRevelations: Array<{ phase: string; texte: string }>;
    vehiculeQuantites: Record<number, number>;
    scenarioKind: 'aucun' | 'tuto' | 'exceptionnelle';
    temps_max: string;
    victimes_min: string; victimes_max: string;
    hostiles_min: string; hostiles_max: string;
    vehicules_acc_min: string; vehicules_acc_max: string;
    recompense_xp: string;
    recompense_credits: string;
  }
  const [preview, setPreview] = useState<PreviewSnapshot | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const refreshPreview = useCallback(() => {
    if (!formRef.current) return;
    const fd = new FormData(formRef.current);
    const totalVehicules = Object.values(vehiculeQuantites).reduce((s, n) => s + n, 0);
    setPreview({
      titre:               String(fd.get('titre') ?? '').trim() || '(titre non renseigné)',
      narrativeAppel,
      narrativeRevelations,
      vehiculeQuantites,
      scenarioKind,
      temps_max:           String(fd.get('temps_max')           ?? '15'),
      victimes_min:        String(fd.get('victimes_min')        ?? '0'),
      victimes_max:        String(fd.get('victimes_max')        ?? '0'),
      hostiles_min:        String(fd.get('hostiles_min')        ?? '0'),
      hostiles_max:        String(fd.get('hostiles_max')        ?? '0'),
      vehicules_acc_min:   String(fd.get('vehicules_accidentes_min') ?? '0'),
      vehicules_acc_max:   String(fd.get('vehicules_accidentes_max') ?? '0'),
      recompense_xp:       String(fd.get('recompense_xp')      ?? '0'),
      recompense_credits:  String(fd.get('recompense_credits')  ?? '0'),
    });
    void totalVehicules; // used below in JSX
    setShowPreview(true);
  }, [formRef, vehiculeQuantites, narrativeAppel, narrativeRevelations, scenarioKind]);

  const {
    categoriesMission,
    loadingOptions,
    configBuildingTypes,
    services,
    vehicules,
    loadingVehicules,
    panelEditDetail,
    loadingEditDetail,
    detailLoadError,
  } = useInterventionFormData({
    isEditMode,
    editingId,
    setEvolutionTaux,
    setVehiculeQuantites,
    setScenarioKind,
    setConditionBatimentRows,
  });

  const conditions = useMemo(
    () => (panelEditDetail?.conditions?.length ? [...panelEditDetail.conditions] : []),
    [panelEditDetail]
  );

  useEffect(() => {
    if (panelEditDetail) {
      setNarrativeAppel(panelEditDetail.narrative_appel ?? '');
      setNarrativeRevelations(panelEditDetail.narrative_revelations ?? []);
    }
  }, [panelEditDetail]);
  const { interventions: interventionsList, loading: loadingInterventions, reload: reloadInterventions } =
    useInterventionsList();

  const d = isEditMode ? panelEditDetail : null;
  const formMountKey = isEditMode
    ? `edit-${editingId}-${d?.id ?? 0}-c${categoriesMission.length}-b${configBuildingTypes.length}-i${loadingInterventions ? 0 : interventionsList.length}`
    : 'create';

  const interventionsForEvolutions = useMemo(
    () => interventionsList.filter((inter) => !isEditMode || inter.id !== editingId),
    [interventionsList, isEditMode, editingId]
  );

  const canShowInterventionForm =
    !isEditMode || (!!panelEditDetail && !loadingEditDetail && !detailLoadError);

  const setVehiculeQuantite = (vehiculeId: number, quantite: number) => {
    setVehiculeQuantites((prev) => ({ ...prev, [vehiculeId]: Math.max(0, quantite) }));
  };

  const vehiculesByService = useMemo(() => {
    return services.map((service) => {
      const list = (vehicules.filter((v) => v.id_service === service.id) as VehiculeConfig[])
        .slice()
        .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
      const byType: Record<string, VehiculeConfig[]> = {};
      list.forEach((v) => {
        const t = v.type || 'Autre';
        if (!byType[t]) byType[t] = [];
        byType[t].push(v);
      });
      const types = Object.keys(byType).sort((a, b) => a.localeCompare(b, 'fr'));
      return { service, byType, types };
    });
  }, [services, vehicules]);

  const setEvolutionTauxFor = (id: number, taux: number) => {
    setEvolutionTaux((prev) => ({ ...prev, [id]: Math.min(100, Math.max(0, taux)) }));
  };

  const toggleEvolution = (id: number, checked: boolean) => {
    setEvolutionTaux((prev) => ({ ...prev, [id]: checked ? 50 : 0 }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(e.currentTarget);
    const result = await submitInterventionScenario({
      formData,
      scenarioKind,
      conditionBatimentRows,
      vehiculeQuantites,
      evolutionTaux,
      isEditMode,
      editingId,
      panelEditDetail,
      conditions,
      narrativeAppel,
      narrativeRevelations,
    });

    if (result.success) {
      setSuccess(result.message ?? null);
      if (isEditMode) {
        void reloadInterventions();
      } else {
        formRef.current?.reset();
        setEvolutionTaux({});
        setVehiculeQuantites({});
        setScenarioKind('aucun');
        setOpenServiceId(null);
        setVehiculeSearchQuery('');
        setConditionBatimentRows([{ config_building_id: 0, quantite_min: 1 }]);
        setNarrativeAppel('');
        setNarrativeRevelations([]);
        void reloadInterventions();
      }
    } else {
      setError(result.error ?? 'Erreur inconnue.');
    }
    setLoading(false);
  };

  return (
    <div className={isEmbedded ? 'h-full overflow-y-auto' : 'min-h-screen relative overflow-hidden'}>
      {!isEmbedded && (
        <>
          <BackgroundImage />
          <ParticlesBackground />
          <Navbar />
        </>
      )}
      <div className={isEmbedded ? '' : 'relative z-10 pt-24 pb-16'}>
        <div className={isEmbedded ? 'max-w-4xl mx-auto' : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'}>
          {!isEmbedded && (
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  {isEditMode ? 'Modifier le scénario d\'intervention' : 'Créer un scénario d\'intervention'}
                </h1>
                <p className="text-slate-400 text-sm">
                  {isEditMode
                    ? `Scénario #${editingId} — mettez à jour les champs puis enregistrez.`
                    : 'Créez un nouveau scénario d\'intervention CTA'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={refreshPreview}
                  className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span>Prévisualiser</span>
                </button>
                <button
                  onClick={() => navigate('/panel')}
                  className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Retour</span>
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
              {success}
            </div>
          )}

          {isEditMode && loadingEditDetail && !detailLoadError && (
            <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-6 mb-4 backdrop-blur-sm">
              <p className="text-slate-300 text-sm">Chargement du scénario...</p>
            </div>
          )}

          {isEditMode && detailLoadError && (
            <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-6 mb-4 backdrop-blur-sm">
              <p className="text-red-300 mb-4 text-sm">{detailLoadError}</p>
              <button
                type="button"
                onClick={() => navigate('/panel/interventions/manage')}
                className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
              >
                Retour à la liste des scénarios
              </button>
            </div>
          )}

          {/* ── Panneau de prévisualisation ── */}
          {showPreview && preview && (
            <div className="mb-6 rounded-xl border border-emerald-500/30 bg-[#061510]/80 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-emerald-500/20 bg-emerald-500/5">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span className="text-sm font-semibold text-emerald-300">Aperçu du scénario</span>
                  <span className="text-[10px] text-emerald-600">— cliquez "Prévisualiser" pour mettre à jour</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="text-slate-500 hover:text-slate-300 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-5 space-y-4">
                {/* Titre + badges */}
                <div className="flex items-start gap-3 flex-wrap">
                  <h3 className="text-lg font-bold text-white flex-1">{preview.titre}</h3>
                  <div className="flex gap-2 flex-wrap">
                    {preview.scenarioKind === 'tuto' && (
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/40 uppercase tracking-wide">
                        🎓 Tutoriel
                      </span>
                    )}
                    {preview.scenarioKind === 'exceptionnelle' && (
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-500/20 text-orange-300 border border-orange-500/40 uppercase tracking-wide">
                        ⚡ Exceptionnelle
                      </span>
                    )}
                  </div>
                </div>

                {/* Message d'appel */}
                {preview.narrativeAppel && (
                  <div className="rounded-lg border border-[#2a3a62] bg-[#0a132b]/80 px-4 py-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">📞 Message d'appel CTA</p>
                    <p className="text-sm text-slate-200 italic">"{preview.narrativeAppel}"</p>
                  </div>
                )}

                {/* Paramètres en grille */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border border-[#1e2d4d] bg-[#0a132b]/60 px-3 py-2.5 text-center">
                    <p className="text-[10px] text-slate-500 mb-1">⏱ Temps max</p>
                    <p className="text-lg font-bold text-slate-200">{preview.temps_max}<span className="text-xs text-slate-400 ml-1">min</span></p>
                  </div>
                  <div className="rounded-lg border border-[#1e2d4d] bg-[#0a132b]/60 px-3 py-2.5 text-center">
                    <p className="text-[10px] text-slate-500 mb-1">👥 Victimes</p>
                    <p className="text-lg font-bold text-slate-200">
                      {preview.victimes_min === preview.victimes_max
                        ? preview.victimes_min
                        : `${preview.victimes_min}–${preview.victimes_max}`}
                    </p>
                  </div>
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-center">
                    <p className="text-[10px] text-amber-600 mb-1">🏆 XP</p>
                    <p className="text-lg font-bold text-amber-300">+{preview.recompense_xp}</p>
                  </div>
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5 text-center">
                    <p className="text-[10px] text-emerald-600 mb-1">💰 Crédits</p>
                    <p className="text-lg font-bold text-emerald-300">+{preview.recompense_credits}€</p>
                  </div>
                </div>

                {/* Véhicules requis */}
                {(() => {
                  const total = Object.values(preview.vehiculeQuantites).reduce((s, n) => s + n, 0);
                  const types = Object.values(preview.vehiculeQuantites).filter(n => n > 0).length;
                  return total > 0 ? (
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <svg className="w-4 h-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l2 2h10M13 16h2m-2 0v-5a1 1 0 011-1h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 01.293.707V16M13 16h2M9 7h4" />
                      </svg>
                      <span>
                        <span className="font-semibold text-white">{total} véhicule{total > 1 ? 's' : ''}</span>
                        {' '}requis — {types} type{types > 1 ? 's' : ''} différent{types > 1 ? 's' : ''}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">Aucun véhicule requis configuré</p>
                  );
                })()}

                {/* Hostiles / véhicules accidentés */}
                {(Number(preview.hostiles_max) > 0 || Number(preview.vehicules_acc_max) > 0) && (
                  <div className="flex gap-4 flex-wrap text-sm">
                    {Number(preview.hostiles_max) > 0 && (
                      <span className="text-slate-400">
                        ⚠️ Hostiles :{' '}
                        <span className="text-red-300 font-semibold">
                          {preview.hostiles_min === preview.hostiles_max ? preview.hostiles_min : `${preview.hostiles_min}–${preview.hostiles_max}`}
                        </span>
                      </span>
                    )}
                    {Number(preview.vehicules_acc_max) > 0 && (
                      <span className="text-slate-400">
                        🚗 Véhicules accidentés :{' '}
                        <span className="text-orange-300 font-semibold">
                          {preview.vehicules_acc_min === preview.vehicules_acc_max ? preview.vehicules_acc_min : `${preview.vehicules_acc_min}–${preview.vehicules_acc_max}`}
                        </span>
                      </span>
                    )}
                  </div>
                )}

                {/* Révélations progressives */}
                {preview.narrativeRevelations.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">📋 Révélations progressives</p>
                    {preview.narrativeRevelations.map((r, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="shrink-0 text-[10px] text-slate-500 border border-[#2a3a62] rounded px-1.5 py-0.5 mt-0.5">
                          {r.phase === 'traveling' ? 'Arrivée' : r.phase === 'on_site' ? 'Sur place' : 'Transport'}
                        </span>
                        <p className="text-sm text-slate-300">"{r.texte}"</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {canShowInterventionForm && (
          <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-6 backdrop-blur-sm">
            <form key={formMountKey} ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelCls}>Titre *</label>
                  <input
                    type="text"
                    name="titre"
                    required
                    defaultValue={d?.titres?.[0] ?? ''}
                    className={inputCls}
                    placeholder="Titre de l'intervention"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelCls}>Catégorie de mission</label>
                  <p className="text-xs text-slate-500 mb-2">
                    Cette catégorie sert à classifier le scénario côté CTA (filtrage, tri et lecture rapide des scénarios par thème).
                  </p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-3">
                    {loadingOptions ? (
                      <p className="text-sm text-slate-500">Chargement...</p>
                    ) : categoriesMission.length === 0 ? (
                      <p className="text-sm text-slate-500">Aucune catégorie. Créez-en dans la base de données.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {categoriesMission.map((item) => (
                          <label key={item.id} className="flex items-center gap-2 cursor-pointer hover:bg-[#1e2d4d]/50 rounded px-2 py-1.5">
                            <input
                              type="checkbox"
                              name="types_mission"
                              value={item.id}
                              defaultChecked={d?.types_mission?.includes(item.id) ?? false}
                              className="w-4 h-4 rounded"
                            />
                            <span className="text-sm text-slate-200">{item.nom || item.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2 rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 space-y-4">
                  <div>
                    <label className={labelCls}>Conditions de déblocage</label>
                    <p className="text-xs text-slate-500 mb-3">
                      Types de bâtiments requis (quantité minimale par type). Les quantités de véhicules pour la mission
                      se règlent dans la section « Véhicules par service » ci-dessous — les compétences du personnel /
                      armement des engins restent hors intervention.
                    </p>
                    <p className="text-xs text-blue-300/90 mb-3">
                      Conseil gameplay : définissez au moins un minimum de véhicules côté mission (ex: 1 véhicule pour le tutoriel)
                      afin de débloquer le premier appel proprement.
                    </p>
                    {loadingOptions ? (
                      <p className="text-sm text-slate-500">Chargement des types de bâtiments…</p>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-slate-400 mb-2">Bâtiments (quantité minimale par type)</p>
                        <div className="space-y-2">
                          {conditionBatimentRows.map((row, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-2">
                              <select
                                value={row.config_building_id || ''}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10) || 0;
                                  setConditionBatimentRows((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], config_building_id: v };
                                    return next;
                                  });
                                }}
                                className="flex-1 min-w-[200px] rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
                              >
                                <option value="">— Type de bâtiment —</option>
                                {configBuildingTypes.map((bt) => (
                                  <option key={bt.id} value={bt.id}>
                                    {bt.nom}
                                    {bt.code ? ` (${bt.code})` : ''}
                                  </option>
                                ))}
                              </select>
                              <label className="flex items-center gap-1 text-sm text-slate-400">
                                min.
                                <input
                                  type="number"
                                  min={1}
                                  value={row.quantite_min}
                                  onChange={(e) => {
                                    const q = parseInt(e.target.value, 10) || 1;
                                    setConditionBatimentRows((prev) => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], quantite_min: Math.max(1, q) };
                                      return next;
                                    });
                                  }}
                                  className="w-16 rounded border border-[#2a3a62] bg-[#0a132b] px-2 py-1.5 text-sm text-slate-100 focus:outline-none"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  setConditionBatimentRows((prev) => prev.filter((_, i) => i !== idx))
                                }
                                disabled={conditionBatimentRows.length <= 1}
                                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40 px-2"
                              >
                                Retirer
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setConditionBatimentRows((prev) => [
                              ...prev,
                              { config_building_id: 0, quantite_min: 1 },
                            ])
                          }
                          className="mt-2 text-sm text-[#8ea0f5] hover:text-[#aabbff]"
                        >
                          + Ajouter un type de bâtiment
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className={labelCls}>Évolutions de mission</label>
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-3">
                    {loadingInterventions ? (
                      <p className="text-sm text-slate-500">Chargement de la liste des interventions...</p>
                    ) : interventionsForEvolutions.length === 0 ? (
                      <p className="text-sm text-slate-500">Aucune intervention existante. Créez d'abord des scénarios (liste du panel) pour pouvoir les choisir comme évolution.</p>
                    ) : (
                      <div className="space-y-2">
                        {interventionsForEvolutions.map((inter) => {
                          const checked = (evolutionTaux[inter.id] ?? 0) > 0;
                          const taux = evolutionTaux[inter.id] ?? 50;
                          return (
                            <div key={inter.id} className="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-[#1e2d4d]/50">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => toggleEvolution(inter.id, e.target.checked)}
                                className="w-4 h-4 rounded shrink-0"
                              />
                              <span className="text-sm text-slate-200 flex-1 min-w-0 truncate">
                                {inter.titres?.[0] || inter.categorie || `Intervention #${inter.id}`}
                              </span>
                              {checked && (
                                <div className="flex items-center gap-1 shrink-0">
                                  <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={taux}
                                    onChange={(e) => setEvolutionTauxFor(inter.id, parseInt(e.target.value, 10) || 0)}
                                    className="w-14 rounded border border-[#2a3a62] bg-[#0a132b] px-2 py-1 text-sm text-slate-100 focus:outline-none"
                                  />
                                  <span className="text-xs text-slate-400">%</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Cochez les scénarios vers lesquels cette intervention peut évoluer et indiquez le taux de chance (%).</p>
                </div>

                {/* ── Section narrative CTA ── */}
                <div className="md:col-span-2 rounded-lg border border-[#2b3a61] bg-[#0d1b3a]/80 p-4 space-y-4">
                  <div>
                    <label className={labelCls}>Message d'appel CTA <span className="text-slate-600">(optionnel)</span></label>
                    <p className="text-xs text-slate-500 mb-2">
                      Texte affiché en bannière lors du spawn de l'intervention et dans le rapport CTA en jeu. Max 200 caractères.
                    </p>
                    <textarea
                      value={narrativeAppel}
                      onChange={(e) => setNarrativeAppel(e.target.value.slice(0, 200))}
                      rows={2}
                      maxLength={200}
                      className={`${inputCls} resize-none`}
                      placeholder="Ex : Incendie signalé immeuble R+4, fumée visible depuis la rue, plusieurs personnes bloquées"
                    />
                    <p className="text-right text-[11px] text-slate-600 mt-0.5">{narrativeAppel.length}/200</p>
                  </div>

                  <div>
                    <label className={labelCls}>Révélations progressives <span className="text-slate-600">(optionnel — max 3)</span></label>
                    <p className="text-xs text-slate-500 mb-2">
                      Messages débloqués au fur et à mesure de l'avancement de l'intervention, visibles dans le rapport CTA en jeu.
                    </p>
                    <div className="space-y-2">
                      {narrativeRevelations.map((rev, idx) => (
                        <div key={idx} className="flex flex-wrap items-start gap-2">
                          <select
                            value={rev.phase}
                            onChange={(e) => {
                              const next = [...narrativeRevelations];
                              next[idx] = { ...next[idx], phase: e.target.value };
                              setNarrativeRevelations(next);
                            }}
                            className="rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60 shrink-0"
                          >
                            <option value="traveling">À l'arrivée des premiers véhicules</option>
                            <option value="on_site">Quand les secours sont sur place</option>
                            <option value="hospital">Lors du transport hospitalier</option>
                          </select>
                          <input
                            type="text"
                            value={rev.texte}
                            onChange={(e) => {
                              const next = [...narrativeRevelations];
                              next[idx] = { ...next[idx], texte: e.target.value.slice(0, 160) };
                              setNarrativeRevelations(next);
                            }}
                            maxLength={160}
                            className={`${inputCls} flex-1 min-w-[200px]`}
                            placeholder="Ex : 3 personnes en attente à la fenêtre du 3e étage"
                          />
                          <button
                            type="button"
                            onClick={() => setNarrativeRevelations((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-xs text-red-400 hover:text-red-300 px-2 py-2"
                          >
                            Retirer
                          </button>
                        </div>
                      ))}
                    </div>
                    {narrativeRevelations.length < 3 && (
                      <button
                        type="button"
                        onClick={() => setNarrativeRevelations((prev) => [...prev, { phase: 'on_site', texte: '' }])}
                        className="mt-2 text-sm text-[#8ea0f5] hover:text-[#aabbff]"
                      >
                        + Ajouter une révélation
                      </button>
                    )}
                  </div>
                </div>

                <VehiculesByServicePicker
                  loadingVehicules={loadingVehicules}
                  vehiculesByService={vehiculesByService}
                  vehiculeSearchQuery={vehiculeSearchQuery}
                  setVehiculeSearchQuery={setVehiculeSearchQuery}
                  openServiceId={openServiceId}
                  setOpenServiceId={setOpenServiceId}
                  vehiculeQuantites={vehiculeQuantites}
                  setVehiculeQuantite={setVehiculeQuantite}
                />

                <InterventionParamsSection
                  d={d}
                  scenarioKind={scenarioKind}
                  setScenarioKind={setScenarioKind}
                />
              </div>

              <div className="flex justify-between items-center gap-3 pt-4 border-t border-[#1e2d4d] flex-wrap">
                <button
                  type="button"
                  onClick={refreshPreview}
                  className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Prévisualiser le scénario
                </button>
                <div className="flex gap-3">
                  {!isEmbedded && (
                    <button
                      type="button"
                      onClick={() => navigate('/panel')}
                      className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
                    >
                      Annuler
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 disabled:opacity-50 transition-colors"
                  >
                    {loading ? (isEditMode ? 'Enregistrement...' : 'Création...') : isEditMode ? 'Enregistrer les modifications' : 'Créer le scénario'}
                  </button>
                </div>
              </div>
            </form>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterventionsCreate;
